import { MailTemplate } from '../../gql/graphql.ts';
import { PageTitle } from '../PageTitle.tsx';
import { Fieldset } from '../form-components/Fieldset.tsx';
import { InputStateWrapper as HookedInput } from '../form-components/InputStateWrapper.tsx';
import { TextareaStateWrapper } from '../form-components/TextareaStateWrapper.tsx';
import { Button } from '@progress/kendo-react-buttons';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import { useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../gql';
import { BackButton } from '../../shared/BackButton.tsx';
import { ComboBox } from '@progress/kendo-react-dropdowns';

interface MailTemplateEditorProps {
  template: MailTemplate;
  companyId: string;
  onSubmit: (v: MailTemplate) => void;
  templateName?: string;
}

const variableList = [
  'COMPANYID',
  'COMPANYNAME',
  'SEMINARTITLE',
  'SEMINARNUMBER',
  'SEMINARDATETEXT',
  'SEMINARLOCATION',
  'SEMINARSTARTDATE',
  'SEMINARENDDATE',
  'SEMINARDESCRIPTION',
  'SEMINARDESCRIPTION2',
  'SEMINARDESCRIPTION3',
  'SEMINARDESCRIPTION4',
  'SEMINARDESCRIPTION5',
  'SEMINARLOCATIONTEXT',
  'SEMINARPRICETEXT',
  'SEMINARPRICETEXT2',
  'SEMINARPRICETEXT3',
  'SEMINARPRICETEXT4',
  'SEMINARPRICE',
  'SEMINARPRICE2',
  'SEMINARPRICE3',
  'SEMINARPRICE4',
  'SEMINARFREETEXT1',
  'SEMINARFREETEXT2',
  'SEMINARFREETEXT3',
  'SEMINARFREETEXT4',
  'SEMINARFREETEXT5',
  'SEMINARMATERIAL',
  'SEMINARPREREQUISITES',
  'SEMINARMINPARTICIPANTS',
  'SEMINARMAXPARTICIPANTS',
  'SEMINARCURRENTPARTICIPANTS',
  'SEMINARREGISTRATIONDATE',
  'SEMINARWEBINARLINK',
  'SEMINARINFORMATIONEXTERN',
  'SEMINARINFORMATIONINTERN',
];

const MjmlPreviewMutation = graphql(/* GraphQL */ `
  mutation previewMjml(
    $companyId: String!
    $mjmlTemplate: String!
    $seminarId: String
  ) {
    previewMjml(
      companyId: $companyId
      mjmlTemplate: $mjmlTemplate
      seminarId: $seminarId
    ) {
      body
    }
  }
`);

const PublicSeminarsSearchQuery = graphql(/* GraphQL */ `
  query getPublicSeminarsSearchMailTemplate(
    $companyId: String!
    $limit: Int!
    $skip: Int!
    $searchTerm: String
  ) {
    publicSeminars(
      companyId: $companyId
      limit: $limit
      skip: $skip
      searchTerm: $searchTerm
    ) {
      seminars {
        id
        title
        seminarNumber
        startDate
        endDate
      }
      count
    }
  }
`);

export const MailTemplateEditor: React.FC<MailTemplateEditorProps> = ({
  template,
  companyId,
  onSubmit,
  templateName,
}) => {
  const [selectedSeminarId, setSelectedSeminarId] = React.useState<
    string | null
  >(null);
  const [searchInputValue, setSearchInputValue] = React.useState<string>('');
  const [{ data: dataPreview }, getPreview] = useMutation(MjmlPreviewMutation);
  const { control, formState, handleSubmit, reset, getValues } =
    useForm<MailTemplate>({
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

  const [publicSeminarsSearchQueryResult, executeSearchQuery] = useQuery({
    query: PublicSeminarsSearchQuery,
    variables: {
      companyId,
      limit: 10,
      skip: 0,
      ...(searchInputValue ? { searchTerm: searchInputValue } : {}),
    },
    pause: true,
  });

  const refreshPreview = () => {
    getPreview({
      companyId,
      mjmlTemplate: getValues('template') ?? '',
      ...(selectedSeminarId ? { seminarId: selectedSeminarId } : {}),
    });
  };

  const onFormSubmit = (values: MailTemplate) => {
    onSubmit(values);
  };

  // Execute search on initial load
  useEffect(() => {
    if (companyId) {
      executeSearchQuery();
    }
  }, []);

  // Execute search on input change
  useEffect(() => {
    executeSearchQuery();
  }, [searchInputValue]);

  useEffect(() => {
    reset({
      subject: template.subject,
      template:
        template.template ??
        `<mjml><mj-body><mj-text> </mj-text></mj-body></mjml>`,
    });
    refreshPreview();
  }, [template]);

  // Automatically refresh preview when seminar changes
  useEffect(() => {
    refreshPreview();
  }, [selectedSeminarId]);

  const { isValid, isDirty } = formState;

  const formattedSeminars =
    publicSeminarsSearchQueryResult?.data?.publicSeminars.seminars.map(
      (seminar) => ({
        ...seminar,
        startDate: new Date(seminar.startDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        endDate: new Date(seminar.endDate).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        name: `${seminar.title} (${new Date(seminar.startDate).toLocaleDateString('de-DE')} - ${new Date(seminar.endDate).toLocaleDateString('de-DE')}) ${seminar.seminarNumber}`,
      }),
    );

  useEffect(() => {
    if (formattedSeminars?.length && !selectedSeminarId) {
      // Auto-pick first seminar once the search returns results.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSeminarId(formattedSeminars[0].id);
    }
  }, [publicSeminarsSearchQueryResult.data]);

  const variableHtmlList = `<ul class='text-sm'>${variableList.map((variable) => `<li>[[${variable}]]</li>`).join('')}</ul>`;

  return (
    <>
      <PageTitle
        title={`${templateName ? `${templateName}: ` : ''}E-Mail Template editieren`}
      >
        <BackButton href="/admin/mailsettings" />
      </PageTitle>

      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-1 flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend="Vorlage bearbeiten"
          descriptionHtml={`Die E-Mail Vorlagen und Betreffzeilen unterstützen die Template-Sprache MJML. Es stehen die folgenden Variablen zur Verfügung:<br/><br/>
          ${variableHtmlList}`}
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
              <HookedInput
                name={'subject'}
                label={'Betreff'}
                placeholder="Betreff"
                control={control}
                rules={{ required: true }}
                required={true}
              />
              <TextareaStateWrapper
                name={'template'}
                label={'Vorlage'}
                placeholder="Hier MJML Vorlage einfügen"
                control={control}
                rows={30}
              />
            </div>
          </div>
        </Fieldset>
        <Fieldset
          legend="Beispieldaten"
          description="Bitte wählen Sie ein Seminar aus, um die Vorschau mit echten Daten zu füllen."
        >
          <div className="md:col-span-9">
            <ComboBox
              onFocus={() => {
                executeSearchQuery();
              }}
              onChange={(e) => {
                if (e.target.value.id === selectedSeminarId) {
                  return;
                }
                setSelectedSeminarId(e.target.value?.id || null);
              }}
              onFilterChange={(e) => {
                const filterValue = e.filter.value.toLowerCase();
                setSearchInputValue(filterValue);
              }}
              value={
                formattedSeminars?.find((s) => s.id === selectedSeminarId) ||
                null
              }
              ariaLabel="Seminar suchen"
              data={formattedSeminars || []}
              id="seminarPicker"
              placeholder="Seminar suchen..."
              textField="name"
              dataItemKey="id"
              filterable={true}
              clearButton={false}
              className="flex-[2]*:font-inter"
            />
            <div className="mt-2 flex items-center text-sm">
              <HeroIcon
                name="InformationCircle"
                className="mr-2 inline-block h-5 w-5 shrink-0"
              />
              <span>
                Hinweis: Nach der Auswahl eines Seminars klicken sie bitte auf
                "Vorschau aktualisieren".
              </span>
            </div>
          </div>
        </Fieldset>
        <Fieldset
          legend="Vorschau"
          description="Hier kann die obige Vorlage sofort ausgegeben und angezeigt werden."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <Button
              type="button"
              onClick={refreshPreview}
              className="[&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            >
              <HeroIcon name="ArrowPath" className="block h-5 w-5 shrink-0" />
              <div>Vorschau aktualisieren</div>
            </Button>
            <iframe
              className={'h-[800px] w-full rounded-md'}
              // Without a unique key, the iframe will add a History item, when it's loaded, so the back button has to be clicked twice to get back to the last page
              key={selectedSeminarId || 'no-seminar'}
              srcDoc={dataPreview?.previewMjml?.body}
            ></iframe>
          </div>
        </Fieldset>
        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || !isDirty}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
